import Backbone from "pages/common/lib/backbone-helpers";
import bindAll from "lodash/bindAll";
import defer from "lodash/defer";
import isUndefined from "lodash/isUndefined";
import isNumber from "lodash/isNumber";
import isFinite from "lodash/isFinite";
import noUiSlider from "nouislider";
import ResizeObserver from "resize-observer-polyfill";

import { getValues, centerSectionToClip } from "util/slider-utils";
import DOMUtils from "util/dom-utils";
import { templateFunction } from "util/componentAsString";

import TimeControlsTpl from "./timecontrol.tpl";

var TimeControlsView = Backbone.View.extend({
    tagName: `section`,
    className: `grTimeControl grTimeControl__udvr`,
    template: templateFunction(TimeControlsTpl),
    lollipopTemplate: () =>
        `<div class="grLollipop js-timeline-lollipop" data-type="timeline"></div>`,
    events: {
        "mousedown .js-timescale": `onTimescaleMouseDown`,
        "mousedown .js-timeline-lollipop": `onLollipopMouseDown`,
        "mousewheel .js-timeline-wrapper": `onTimelineScroll`,
        "DOMMouseScroll .js-timeline-wrapper": `onTimelineScroll`,
        "click .js-zoom-in": `zoomInCenter`,
        "click .js-zoom-out": `zoomOut`,
        "click .js-zoom-to-selection": `zoomToSelection`
    },

    initialize: function () {
        bindAll(
            this,
            `onTimescaleChange`,
            `onTimescaleStart`,
            `onTimescaleFinish`,
            `onTimescaleSlide`,
            `onTimescaleMouseUp`
        );
        bindAll(
            this,
            `onLollipopDrag`,
            `onLollipopMouseUp`,
            `onFirstSectionBoundariesChange`,
            `onTimelineScroll`,
            `refreshTimescale`
        );

        this.listenTo(
            this.model,
            `change:currentTime`,
            this.onCurrentTimeUpdate
        );
        this.listenTo(
            this.model,
            `change:start change:end`,
            this.onBoundariesChange
        );
        this.listenToOnce(
            this.model,
            `change:sectionEnd change:sectionStart`,
            this.onFirstSectionBoundariesChange
        );

        this.timescaleWidth = 1;
        this.timelineWrapperWidth = 1;

        const observerCallback =
            (fn) =>
            ([
                {
                    contentRect: { width }
                }
            ]) => {
                const percentage = this.getLollipopPercentage();
                fn(width);
                this.updateLollipop(percentage);
            };

        this.timelineObserver = new ResizeObserver(
            observerCallback(
                (width) => (this.timelineWrapperWidth = width)
            ).bind(this)
        );
        this.timescaleObserver = new ResizeObserver(
            observerCallback((width) => (this.timescaleWidth = width)).bind(
                this
            )
        );
    },

    render: function () {
        this.destroyTimescale();

        this.$el.html(this.template(this.model.getTimelineLabels()));

        this.timescale = this.el.querySelector(`.js-timescale`);
        this.timelineWrapper = this.el.querySelector(`.js-timeline-elastic`);

        noUiSlider.create(this.timescale, {
            start: this.model.getTimescaleInitialSection(),
            range: {
                min: [this.model.get(`start`)],
                max: [this.model.get(`end`)]
            },
            connect: true,
            step: 1,
            animate: false,
            behaviour: `drag`
        });

        this.timescale.noUiSlider.on(`start`, this.onTimescaleStart);
        this.timescale.noUiSlider.on(`end`, this.onTimescaleFinish);
        this.timescale.noUiSlider.on(`slide`, this.onTimescaleChange);
        this.timescale.noUiSlider.on(`slide`, this.onTimescaleSlide);

        this.$wrapper = this.$(`.js-timeline-wrapper`);
        this.$timelineWrapper = this.$(`.js-timeline-elastic`);
        this.$timescaleWrapper = this.$(`.js-timescale-wrapper`);
        this.$labelStart = this.$(`.js-label-start`);
        this.$labelEnd = this.$(`.js-label-end`);

        this.$timelineWrapper.append(this.lollipopTemplate());

        this.timescaleSliderActive = false;
        this.timescaleSliderSlide = false;

        this.lollipops = this.el.querySelectorAll(`.js-timeline-lollipop`);

        this.initObservers();

        return this;
    },

    onTimescaleChange: function () {
        var timescaleValues = getValues(this.timescale.noUiSlider);

        var timelineTimeLength =
            this.model.get(`end`) - this.model.get(`start`);
        var sectionTimeLength = timescaleValues[1] - timescaleValues[0];

        var deltaFrom = timescaleValues[0] - this.model.get(`start`);

        var sectionWidthPercentage = sectionTimeLength / timelineTimeLength;
        var sectionOffsetPercentage = deltaFrom / timelineTimeLength;
        var timelineWidth = 1 / sectionWidthPercentage;
        var timelineOffset = sectionOffsetPercentage * timelineWidth * -1;

        this.$timelineWrapper[0].style.width = `${timelineWidth * 100}%`;
        this.$timelineWrapper[0].style.left = `${timelineOffset * 100}%`;

        this.model.set({
            sectionStart: timescaleValues[0],
            sectionEnd: timescaleValues[1]
        });

        this.onCurrentTimeUpdate(this.model, this.model.get(`currentTime`));
        App.Events.trigger(`timeline:zoom`);
    },

    onCurrentTimeUpdate: function (model, newCurrentTime = 0) {
        if (this.lollipopActive) {
            return;
        }

        var timeLength = this.model.get(`end`) - this.model.get(`start`);

        var leftOffset = newCurrentTime / timeLength;

        leftOffset = leftOffset < 0 ? 0 : leftOffset;
        leftOffset = leftOffset > 1 ? 1 : leftOffset;
        this.updateLollipop(leftOffset);
    },

    onTimescaleMouseDown: function () {
        App.Utils.$window.on(`mouseup`, this.onTimescaleMouseUp);
    },

    onTimescaleStart: function () {
        this.timescaleSliderActive = true;
        this.timescaleSliderSlide = false;
    },

    onTimescaleFinish: function () {
        this.timescaleSliderActive = false;
    },

    onTimelineStart: function () {
        this.trigger(`drag:start`);
    },

    onTimelineFinish: function () {
        TimeControlsView.__super__.onTimelineFinish.apply(this, arguments);
        this.timelineSliderActive = false;
        var _this = this;
        var args = arguments;
        defer(function () {
            _this.timelineSliderSlide = false;
            _this.onTimelineSlide.apply(_this, args);
        });
        this.trigger(`drag:stop`);
    },

    onTimescaleSlide: function () {
        App.Events.trigger(`timeline:zoom`);
        this.timescaleSliderSlide = true;
        if (!this.timescaleSliderActive) {
            this.timescaleSliderActive = true;
        }
    },

    onTimescaleMouseUp: function () {
        var _this = this;
        defer(function () {
            _this.timescaleSliderActive = false;
            _this.timescaleSliderSlide = false;
        });
        App.Utils.$window.off(`mouseup`, this.onTimescaleMouseUp);
    },

    onBoundariesChange: function (model) {
        var updateOptions = {
            range: {
                min: [
                    !isUndefined(model.get(`start`)) ? model.get(`start`) : 0
                ],
                max: [!isUndefined(model.get(`end`)) ? model.get(`end`) : 0]
            }
        };

        if (!this.timescaleSliderActive) {
            var sliderMax = this.model.previous(`end`);
            var toValue = getValues(this.timescale.noUiSlider)[1];

            this.timescale.noUiSlider.updateOptions(updateOptions);

            var isInLiveRegion = toValue === sliderMax;
            if (isInLiveRegion) {
                this.timescale.noUiSlider.set([null, model.get(`end`)]);
            }
        }

        this.updateTimelineLabels();
        this.onTimescaleChange();

        return updateOptions;
    },

    onFirstSectionBoundariesChange: function (model) {
        // When a page is initialized with a default sectionStart and sectionEnd it sets the uiSlider and timescale
        if (
            !isUndefined(model.get(`sectionStart`)) &&
            !isUndefined(model.get(`sectionEnd`))
        ) {
            const updateOptions = {
                range: {
                    min: [
                        !isUndefined(model.get(`start`))
                            ? model.get(`start`)
                            : 0
                    ],
                    max: [!isUndefined(model.get(`end`)) ? model.get(`end`) : 0]
                }
            };

            updateOptions[`start`] = [
                model.get(`sectionStart`),
                model.get(`sectionEnd`)
            ];

            this.timescale.noUiSlider.updateOptions(updateOptions);
            this.onTimescaleChange();

            return updateOptions;
        }
    },

    zoomToSelection: function (offset) {
        offset = isNumber(offset) && offset > 0 ? offset : 0;

        let newFrom = this.model.get(`inPoint`) - offset;
        newFrom =
            newFrom > this.model.get(`start`)
                ? newFrom
                : this.model.get(`start`);

        let newTo = this.model.get(`outPoint`) + offset;
        newTo = newTo < this.model.get(`end`) ? newTo : this.model.get(`end`);

        this.refreshTimescale(newFrom, newTo);
    },

    zoomOut: function () {
        this.zoom(2);
    },

    zoomIn: function () {
        this.zoom(0.5);
    },

    zoomInCenter: function () {
        this.zoom(0.5, true);
    },

    zoom: function (ratio, centerToClip) {
        if (this.timescale.hasAttribute(`disabled`)) {
            return;
        }

        let inPoint = this.model.get(`sectionStart`);
        let outPoint = this.model.get(`sectionEnd`);
        const inPointFinite = isFinite(inPoint);
        const outPointFinite = isFinite(outPoint);
        const start = this.model.get(`start`);
        const end = this.model.get(`end`);

        if (!inPointFinite && outPointFinite) {
            inPoint = outPoint;
        } else if (inPointFinite && !outPointFinite) {
            outPoint = inPoint;
        } else if (!inPointFinite && !outPointFinite) {
            throw new Error(`inPoint and outPoint non finite value`);
        }

        const newSectionLength = this.getNewSectionLength(ratio, centerToClip);
        const centeredSectionBoundaries = centerSectionToClip(
            newSectionLength,
            inPoint,
            outPoint,
            start,
            end
        );

        this.refreshTimescale(
            centeredSectionBoundaries.start,
            centeredSectionBoundaries.end
        );
        App.Events.trigger(`timeline:zoom`);
    },

    getNewSectionLength: function (ratio) {
        var sectionStart = this.model.get(`sectionStart`);
        var sectionEnd = this.model.get(`sectionEnd`);
        // Minimum of a zoomed section is .5s
        const sectionLength = (sectionEnd - sectionStart) * ratio;
        return sectionLength < 500 ? 500 : sectionLength;
    },

    refreshTimescale: function (sliderFrom, sliderTo) {
        this.model.set({
            sectionStart: sliderFrom,
            sectionEnd: sliderTo
        });

        if (!this.timescaleSliderActive) {
            this.timescale.noUiSlider.set([sliderFrom, sliderTo]);
        }

        this.onTimescaleChange();
    },

    updateTimelineLabels: function () {
        var labels = this.model.getTimelineLabels();
        this.$labelStart.text(labels.startLabel);
        this.$labelEnd.text(labels.endLabel);
    },

    onTimelineScroll: function (e) {
        // We check if it's horizontal scrolling
        var evt = e.originalEvent;
        if (
            evt.wheelDeltaX ||
            (`axis` in evt &&
                `HORIZONTAL_AXIS` in evt &&
                evt.axis === evt.HORIZONTAL_AXIS)
        ) {
            e.preventDefault();

            // Logic to convert wheelData to pixels taken from
            // https://github.com/eligrey/RetargetMouseScroll/blob/master/RetargetMouseScroll.js

            var scrollAmount = evt.detail || -evt.wheelDeltaX / 40; // convert wheelData to lines
            scrollAmount *= 19; // convert lines to pixels

            var wrapperWidth = this.$wrapper.width();
            var percentage =
                1 - (wrapperWidth - Math.abs(scrollAmount)) / wrapperWidth;

            var sectionDuration = this.model.getSectionDuration();
            var delta = sectionDuration * percentage;

            var newSectionStart = 0;
            var newSectionEnd = 0;
            if (scrollAmount < 0) {
                var sectionStart = this.model.get(`sectionStart`);
                newSectionStart =
                    sectionStart - delta >= 0 ? sectionStart - delta : 0;
                newSectionEnd = newSectionStart + sectionDuration;
            } else {
                var sectionEnd = this.model.get(`sectionEnd`);
                newSectionEnd =
                    sectionEnd + delta <= this.model.get(`end`)
                        ? sectionEnd + delta
                        : this.model.get(`end`);
                newSectionStart = newSectionEnd - sectionDuration;
            }

            this.timescale.noUiSlider.set([newSectionStart, newSectionEnd]);
            this.onTimescaleChange();
        }
    },

    onLollipopMouseDown: function (evt) {
        App.Utils.$window.on(`mousemove`, this.onLollipopDrag);
        App.Utils.$window.on(`mouseup`, this.onLollipopMouseUp);
        this.lollipopActive = evt.currentTarget.getAttribute(`data-type`);
    },

    onLollipopMouseUp: function () {
        App.Utils.$window.off(`mousemove`, this.onLollipopDrag);
        App.Utils.$window.off(`mouseup`, this.onLollipopMouseUp);
        this.lollipopActive = false;

        var time = this.getLollipopTime();

        this.model.set({
            currentTime: time,
            currentTimeLocked: true
        });
        this.model.trigger(`update:currentTime`, time);
    },

    onLollipopDrag: function (evt) {
        var leftValue = 0;
        if (this.lollipopActive === `timeline`) {
            var timeHovered =
                DOMUtils.getTimeHovered.call(this, evt.pageX) -
                this.model.get(`start`);
            var videoLength = this.model.getTimelineDuration();
            leftValue = timeHovered / videoLength;
        } else if (this.lollipopActive === `timescale`) {
            leftValue = DOMUtils.getPercentageHovered(
                evt.pageX,
                this.$timescaleWrapper
            );
        }

        leftValue = leftValue < 0 ? 0 : leftValue;
        leftValue = leftValue > 1 ? 1 : leftValue;
        this.updateLollipop(leftValue);
    },

    getLollipopPercentage: function () {
        const translateMatcher = /\((.*?)px/;
        const matchedValue = translateMatcher.exec(
            this.lollipops[0].style.transform
        );
        if (matchedValue !== null) {
            return matchedValue[1] / this.timelineWrapperWidth;
        } else {
            return 0;
        }
    },

    getSelectionDuration: function () {
        // Need to decide if this enough to handle the case
        // of one/both points being undefined
        return this.model.get(`outPoint`) - this.model.get(`inPoint`) || 0;
    },

    getLollipopTime: function () {
        var videoDuration = this.model.getTimelineDuration();
        return videoDuration * this.getLollipopPercentage();
    },

    getLollipopTimestamp: function () {
        return this.getLollipopTime() + this.model.get(`start`);
    },

    destroyTimescale: function () {
        if (this.timescale && this.timescale.noUiSlider) {
            this.timescale.noUiSlider.off();
            this.timescale.noUiSlider.destroy();
        }
    },

    destroy: function () {
        this.destroyTimescale();
        TimeControlsView.__super__.destroy.apply(this, arguments);
    },

    initObservers: function () {
        this.timelineObserver.observe(this.timelineWrapper);
        this.timescaleObserver.observe(this.timescale);
    },

    updateLollipop: function (leftPercentage) {
        for (const lollipop of this.lollipops) {
            const containerWidth =
                lollipop.dataset.type === `timeline`
                    ? this.timelineWrapperWidth
                    : this.timescaleWidth;
            const translateValue = containerWidth * leftPercentage;
            lollipop.style.transform = `translateX(${translateValue}px)`;
        }
    }
});

export default TimeControlsView;
